import {
  Users,
  Stethoscope,
  Activity,
  Clock,
  FlaskConical,
  ScanLine,
  Pill,
  Wallet,
} from "lucide-react";
import {
  HeaderKpi,
  SectionCard,
  StatTile,
  RowItem,
  CenterHeader,
} from "./center/CenterPrimitives";

export default function ClinicalOpsCenterView({ onBack, onOpenSub }: { onBack: () => void; onOpenSub?: (id: string) => void }) {
  return (
    <div className="flex flex-col gap-5">
      <CenterHeader
        title="Clinical Operations Center"
        scope="Patients · Soins · Labo · Imagerie · Pharmacie · Facturation"
        onBack={onBack}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <HeaderKpi icon={Users} value="104" label="Patients admis aujourd'hui" delta="0%" tone="neutral" seed={1} />
        <HeaderKpi icon={Stethoscope} value="18" label="Consultations actives" delta="+20%" tone="success" seed={2} />
        <HeaderKpi icon={Activity} value="77%" label="Taux occupation lits" delta="0%" tone="neutral" seed={3} />
        <HeaderKpi icon={Clock} value="23min" label="Délai moyen consultation" delta="0%" tone="neutral" seed={4} />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <SectionCard title="Patient Flow" scope="Admissions · Consultations · Agenda · Sorties" icon={Users} tone="success">
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="Admissions" value="4" hint="Aujourd'hui" />
            <StatTile label="Consultations" value="18" hint="Aujourd'hui" />
            <StatTile label="Lits occupés" value="62/80" hint="77% occupation" />
            <StatTile label="Sorties" value="6" hint="Aujourd'hui" />
          </div>
          <div className="mt-3 space-y-2">
            <RowItem title="Patients" hint="Dossiers · timeline" />
            <RowItem title="Consultation" hint="Notes · IA ICD-10" />
            <RowItem title="Agenda" hint="Planifier un RDV" />
            <RowItem title="E-Notes" hint="FSE · ALD · Médicaments" />
          </div>
        </SectionCard>

        <SectionCard title="Laboratory" scope="Résultats · Analyseurs · Middleware · TAT" icon={FlaskConical} tone="success">
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="En attente" value="12" hint="échantillons" />
            <StatTile label="Validés (7j)" value="1432" hint="résultats" />
            <StatTile label="Critiques" value="2" tone="warning" hint="K+ 6.8 · Hématies 8.82 H" />
            <StatTile label="TAT moyen" value="2h 14m" hint="SLA 4h00" />
          </div>
          <div className="mt-3 space-y-2">
            <RowItem title="Consultation" hint="Notes · IA ICD-10" />
            <RowItem title="Résultats" hint="LOINC · validation · NC" />
            <RowItem title="Analyseurs" hint="Roche Cobas · Siemens" />
            <RowItem title="Middleware" hint="HL7 / ASTM routing" />
            <RowItem title="Vue patient" hint="Résultats par dossier" />
          </div>
        </SectionCard>

        <SectionCard title="Radiology" scope="PACS · Worklists · IA · DICOM" icon={ScanLine} tone="success">
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="Études PACS" value="24 891" hint="total stockées" />
            <StatTile label="Worklist" value="23" hint="à lire" />
            <StatTile label="IA en cours" value="4" hint="détection nodules" />
            <StatTile label="Délai lecture" value="1h 02m" hint="SLA 2h00" />
          </div>
          <div className="mt-3 space-y-2">
            <RowItem title="PACS" hint="Orthanc · dcm4chee" />
            <RowItem title="Worklists" hint="MWL scheduled" />
            <RowItem title="IA Imagerie" hint="Nodules · fractures · AVC" />
            <RowItem title="Études patient" hint="Viewer DICOM" />
          </div>
        </SectionCard>

        <SectionCard title="Pharmacy" scope="Stocks · Dispensation · Traçabilité · Contrôles" icon={Pill} tone="warning">
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="Dispensations (7j)" value="50" hint="actes pharmacie" />
            <StatTile label="Stocks critiques" value="3" tone="warning" hint="Fentanyl · Morphine" />
            <StatTile label="Stupéfiants" value="5" hint="sous contrôle" />
            <StatTile label="Taux dispensation" value="98.7%" hint="automatique" />
          </div>
          <div className="mt-3 space-y-2">
            <RowItem title="Stocks" hint="Niveaux · seuils" onClick={() => onOpenSub?.("pharmacy-stocks")} />
            <RowItem title="Dispensation" hint="Actes nominatifs" onClick={() => onOpenSub?.("pharmacy-dispensation")} />
            <RowItem title="Traçabilité" hint="Numéro de lot" onClick={() => onOpenSub?.("pharmacy-tracabilite")} />
            <RowItem title="Vue patient" hint="Prescriptions actives" onClick={() => onOpenSub?.("pharmacy-vue-patient")} />
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Billing & Revenue" scope="CNSS · Claims · Forfaits · FSE" icon={Wallet} tone="success">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <StatTile label="Revenus (7j)" value="48 750 MAD" hint="CNSS + privé" tone="success" />
          <StatTile label="Claims en cours" value="30" hint="à soumettre" />
          <StatTile label="Taux remboursement" value="82%" hint="CNSS" />
          <StatTile label="Rejets (30j)" value="4" tone="warning" hint="à corriger" />
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <RowItem title="Facturation" hint="Actes · paiements" />
          <RowItem title="Claims CNSS" hint="Soumissions batch" />
          <RowItem title="Codage NGAP" hint="Actes techniques" />
          <RowItem title="CIM-10" hint="Codage diagnostic" />
        </div>
      </SectionCard>
    </div>
  );
}
